import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { useIsMobile } from "@/components/ui/use-mobile";
import { getCategoryIconFallback } from "../api/landingClass.mappers";
import CatalogCourseCard from "../components/CatalogCourseCard";
import CatalogInfiniteList from "../components/CatalogInfiniteList";
import CatalogPagination from "../components/CatalogPagination";
import CategoryHero from "../components/CategoryHero";
import CourseFilterBar from "../components/CourseFilterBar";
import { LandingErrorState, LandingLoadingState } from "../components/LandingState";
import { LANDING_USE_MOCK } from "../config/landing.config";
import {
  COURSES_PER_PAGE,
  LANDING_CATALOG_GRID,
  LANDING_PAGE_GUTTER,
  LANDING_TYPOGRAPHY,
} from "../constants/landing.constants";
import {
  getCategoryMeta,
  getCoursesByCategory,
  isKnownCategorySlug,
} from "../data/landing.mock";
import { useCatalogFilters } from "../hooks/useCatalogFilters";
import { useClassesByLevel } from "../hooks/useClassesByLevel";
import { collectCatalogAgeOptions } from "../utils/catalogAgeFilter.utils";
import {
  buildCatalogListingSearchParams,
  parseCatalogListingParams,
  parseCatalogSortValue,
  type CatalogListingParams,
} from "../utils/catalogUrlParams";
import {
  saveLandingListingQuery,
} from "../utils/landingListingQuery";
import { resolveCategorySlugToLevel2 } from "../utils/catalogSlugResolve";
import type { LandingCourse } from "../types/landing.types";

function CatalogListSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className={`pb-12 pt-2 ${LANDING_PAGE_GUTTER}`}>
      <h2 className={LANDING_TYPOGRAPHY.sectionTitleCatalog}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function MockCategoryListingPage() {
  const { categorySlug = "" } = useParams();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sortBy, search, ageFilter, page } =
    parseCatalogListingParams(searchParams);
  const listingQuery = searchParams.toString();
  const isValidCategory = isKnownCategorySlug(categorySlug);
  const meta = getCategoryMeta(categorySlug);
  const courses = getCoursesByCategory(categorySlug);

  const updateListingParams = (
    patch: Partial<Pick<CatalogListingParams, "sortBy" | "search" | "ageFilter" | "page">>
  ) => {
    const current = parseCatalogListingParams(searchParams);
    setSearchParams(
      buildCatalogListingSearchParams({
        level1Id: current.level1Id,
        sortBy: patch.sortBy ?? current.sortBy,
        search: patch.search ?? current.search,
        ageFilter: patch.ageFilter ?? current.ageFilter,
        page: patch.page ?? current.page,
      }),
      { replace: true }
    );
  };

  const {
    setSearch,
    setAgeFilter,
    setSortBy,
    setPage,
    totalPages,
    filtered,
    paginated,
    clearAgeFilter,
  } = useCatalogFilters({
    courses,
    pageSize: COURSES_PER_PAGE,
    sortBy,
    setSortBy: (value) => {
      updateListingParams({ sortBy: parseCatalogSortValue(value), page: 1 });
    },
    search,
    setSearch: (value) => updateListingParams({ search: value, page: 1 }),
    ageFilter,
    setAgeFilter: (value) => updateListingParams({ ageFilter: value, page: 1 }),
    page,
    setPage: (nextPage) => updateListingParams({ page: nextPage }),
  });

  const ageOptions = useMemo(() => collectCatalogAgeOptions(courses), [courses]);

  useEffect(() => {
    if (categorySlug && listingQuery) {
      saveLandingListingQuery(categorySlug, listingQuery);
    }
  }, [categorySlug, listingQuery]);

  if (!isValidCategory) {
    return <Navigate to="/classes" replace />;
  }

  return (
    <>
      <CategoryHero
        badge={meta.badge}
        title={meta.title}
        heroImage={meta.heroImage}
        onBack={() => navigate("/classes")}
      />

      <CourseFilterBar
        categoryTitle={meta.title}
        search={search}
        onSearchChange={setSearch}
        ageFilter={ageFilter}
        onAgeFilterChange={setAgeFilter}
        ageOptions={ageOptions}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onClearAgeFilter={clearAgeFilter}
      />

      <CatalogListSection title={`All ${meta.title}`}>
        {paginated.length === 0 ? (
          <p className="py-16 text-center text-[16px] text-landing-muted">
            No courses found. Try adjusting your filters.
          </p>
        ) : isMobile ? (
          <CatalogInfiniteList
            dataLength={Math.min(page * COURSES_PER_PAGE, filtered.length)}
            hasMore={page < totalPages}
            next={() => setPage(page + 1)}
            gridClassName={LANDING_CATALOG_GRID}
          >
            {filtered
              .slice(0, page * COURSES_PER_PAGE)
              .map((course) => (
                <CatalogCourseCard
                  key={course.id}
                  course={course}
                  listingQuery={listingQuery}
                  listingScopeId={categorySlug}
                />
              ))}
          </CatalogInfiniteList>
        ) : (
          <div className={LANDING_CATALOG_GRID}>
            {paginated.map((course) => (
              <CatalogCourseCard
                key={course.id}
                course={course}
                listingQuery={listingQuery}
                listingScopeId={categorySlug}
              />
            ))}
          </div>
        )}

        {!isMobile && (
          <CatalogPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </CatalogListSection>
    </>
  );
}

function ApiCategoryListingPage() {
  const { categorySlug = "", levelId: legacyLevelId = "" } = useParams();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { level1Id, sortBy, search, ageFilter, page } =
    parseCatalogListingParams(searchParams);
  const listingQuery = searchParams.toString();

  const [resolvedLevelId, setResolvedLevelId] = useState(legacyLevelId);
  const [resolvedLevel1Id, setResolvedLevel1Id] = useState(level1Id);
  const [resolveLoading, setResolveLoading] = useState(
    !legacyLevelId && Boolean(categorySlug)
  );
  const [resolveFailed, setResolveFailed] = useState(false);

  useEffect(() => {
    if (legacyLevelId) {
      setResolvedLevelId(legacyLevelId);
      setResolvedLevel1Id(level1Id);
      setResolveFailed(false);
      setResolveLoading(false);
      return;
    }

    if (!categorySlug) {
      setResolvedLevelId("");
      setResolveFailed(true);
      setResolveLoading(false);
      return;
    }

    let cancelled = false;
    setResolveLoading(true);
    setResolveFailed(false);

    void resolveCategorySlugToLevel2(
      categorySlug,
      level1Id || undefined
    ).then((resolved) => {
      if (cancelled) return;

      if (!resolved) {
        setResolvedLevelId("");
        setResolveFailed(true);
        setResolveLoading(false);
        return;
      }

      setResolvedLevelId(resolved.level2Id);
      // URL level1Id wins when present; otherwise use resolved parent
      setResolvedLevel1Id(level1Id || resolved.level1Id);
      setResolveLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [categorySlug, legacyLevelId, level1Id]);

  const effectiveLevel1Id = level1Id || resolvedLevel1Id;
  const listingScopeId = categorySlug || resolvedLevelId;

  useEffect(() => {
    if (listingScopeId && listingQuery) {
      saveLandingListingQuery(listingScopeId, listingQuery);
    }
  }, [listingScopeId, listingQuery]);

  const updateListingParams = (
    patch: Partial<Pick<CatalogListingParams, "sortBy" | "search" | "ageFilter" | "page">>
  ) => {
    const current = parseCatalogListingParams(searchParams);
    setSearchParams(
      buildCatalogListingSearchParams({
        level1Id: current.level1Id,
        sortBy: patch.sortBy ?? current.sortBy,
        search: patch.search ?? current.search,
        ageFilter: patch.ageFilter ?? current.ageFilter,
        page: patch.page ?? current.page,
      }),
      { replace: true }
    );
  };

  const { courses, heroBadge, heroTitle, totalPages, isLoading, error } =
    useClassesByLevel({
      levelId: resolvedLevelId,
      level1Id: effectiveLevel1Id || undefined,
      search,
      ageFilter,
      sortBy,
      page,
    });

  // Mobile infinite scroll accumulates fetched pages; page resets to 1 on any
  // filter change (see updateListingParams), which resets the accumulation.
  const [mobileCourses, setMobileCourses] = useState<LandingCourse[]>([]);
  useEffect(() => {
    if (!isMobile || isLoading) return;
    setMobileCourses((prev) => {
      if (page <= 1) return courses;
      const seen = new Set(prev.map((c) => c.id));
      return [...prev, ...courses.filter((c) => !seen.has(c.id))];
    });
  }, [isMobile, isLoading, page, courses]);

  const ageOptions = useMemo(() => collectCatalogAgeOptions(courses), [courses]);

  const clearAgeFilter = () => {
    updateListingParams({ ageFilter: "", page: 1 });
  };

  if (resolveLoading) {
    return <LandingLoadingState message="Loading classes…" />;
  }

  if (resolveFailed || !resolvedLevelId) {
    return <Navigate to="/classes" replace />;
  }

  const listTitle = heroTitle || "Classes";

  return (
    <>
      <CategoryHero
        badge={heroBadge || "Class"}
        title={listTitle}
        heroImage={getCategoryIconFallback(heroBadge)}
        onBack={() => navigate("/classes")}
      />

      <CourseFilterBar
        categoryTitle={listTitle}
        search={search}
        onSearchChange={(value) => {
          updateListingParams({ search: value, page: 1 });
        }}
        ageFilter={ageFilter}
        onAgeFilterChange={(value) => {
          updateListingParams({ ageFilter: value, page: 1 });
        }}
        ageOptions={ageOptions}
        sortBy={sortBy}
        onSortChange={(value) => {
          updateListingParams({ sortBy: parseCatalogSortValue(value), page: 1 });
        }}
        onClearAgeFilter={clearAgeFilter}
      />

      <CatalogListSection title={`All ${listTitle}`}>
        {isLoading && (!isMobile || mobileCourses.length === 0) ? (
          <LandingLoadingState message="Loading classes…" />
        ) : error ? (
          <LandingErrorState message={error} />
        ) : isMobile ? (
          mobileCourses.length === 0 ? (
            <p className="py-16 text-center text-[16px] text-landing-muted">
              No courses found. Try adjusting your filters.
            </p>
          ) : (
            <CatalogInfiniteList
              dataLength={mobileCourses.length}
              hasMore={page < totalPages}
              next={() => updateListingParams({ page: page + 1 })}
              gridClassName={LANDING_CATALOG_GRID}
            >
              {mobileCourses.map((course) => (
                <CatalogCourseCard
                  key={course.id}
                  course={course}
                  levelId={resolvedLevelId}
                  level1Id={effectiveLevel1Id || undefined}
                  listingQuery={listingQuery}
                  listingScopeId={listingScopeId}
                />
              ))}
            </CatalogInfiniteList>
          )
        ) : courses.length === 0 ? (
          <p className="py-16 text-center text-[16px] text-landing-muted">
            No courses found. Try adjusting your filters.
          </p>
        ) : (
          <div className={LANDING_CATALOG_GRID}>
            {courses.map((course) => (
              <CatalogCourseCard
                key={course.id}
                course={course}
                levelId={resolvedLevelId}
                level1Id={effectiveLevel1Id || undefined}
                listingQuery={listingQuery}
                listingScopeId={listingScopeId}
              />
            ))}
          </div>
        )}

        {!isMobile && (
          <CatalogPagination
            page={page}
            totalPages={totalPages}
            onPageChange={(nextPage) => updateListingParams({ page: nextPage })}
          />
        )}
      </CatalogListSection>
    </>
  );
}

export default function CategoryListingPage() {
  return LANDING_USE_MOCK ? (
    <MockCategoryListingPage />
  ) : (
    <ApiCategoryListingPage />
  );
}
